import React, { useMemo, useState } from 'react';
import { DownloadIcon, LandmarkIcon } from 'lucide-react';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { formatKwacha, formatPeriod, sumKwacha } from '../../../utils/hrFormat';
import { exportCsv, exportExcel, exportPdf, ExportColumn, ExportRow } from '../../../utils/hrExport';
import { Button } from '../../../components/ui/Button';
type Kind = 'paye' | 'napsa' | 'nhima' | 'employer';
const kinds: {
  id: Kind;
  label: string;
}[] = [{
  id: 'paye',
  label: 'PAYE (ZRA)'
}, {
  id: 'napsa',
  label: 'NAPSA'
}, {
  id: 'nhima',
  label: 'NHIMA'
}, {
  id: 'employer',
  label: 'Employer levies'
}];

/** Statutory returns built from processed (or paid) payroll only. */
export function StatutoryContributions() {
  const {
    payrollRuns
  } = useHrPayroll();
  const finalised = useMemo(() => payrollRuns.filter(r => r.status === 'Processed' || r.status === 'Paid').sort((a, b) => b.period.localeCompare(a.period)), [payrollRuns]);
  const [runId, setRunId] = useState('');
  const [kind, setKind] = useState<Kind>('paye');
  const run = finalised.find(r => r.id === runId) ?? finalised[0];
  if (!run) {
    return <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-id="element-1869" data-mp="eg9kf0yvxddu">
        <LandmarkIcon className="mx-auto h-10 w-10 text-slate-300" aria-hidden="true" data-id="element-1870" data-mp="9h5au981tdmz" />
        <p className="mt-2 font-semibold text-slate-800" data-id="element-1871" data-mp="9jj3kbf9adrd">No processed payroll yet</p>
        <p className="text-sm text-slate-500" data-id="element-1872" data-mp="4k6jfyfrrksh">Statutory returns appear here once a payroll has been processed with PAY EMPLOYEES.</p>
      </div>;
  }
  const t = run.totals;
  const summary = [{
    label: 'PAYE due to ZRA',
    value: t.paye,
    note: 'Employee tax withheld'
  }, {
    label: 'NAPSA total',
    value: sumKwacha([t.napsaEmployee, t.napsaEmployer]),
    note: `${formatKwacha(t.napsaEmployee)} employee + ${formatKwacha(t.napsaEmployer)} employer`
  }, {
    label: 'NHIMA total',
    value: sumKwacha([t.nhimaEmployee, t.nhimaEmployer]),
    note: `${formatKwacha(t.nhimaEmployee)} employee + ${formatKwacha(t.nhimaEmployer)} employer`
  }, {
    label: 'SDL + Workers’ Comp',
    value: sumKwacha([t.sdl, t.workersComp]),
    note: 'Employer-only costs'
  }];
  const config: Record<Kind, {
    columns: ExportColumn[];
    rows: ExportRow[];
  }> = {
    paye: {
      columns: [{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'tpin',
        label: 'TPIN'
      }, {
        key: 'gross',
        label: 'Gross',
        align: 'right'
      }, {
        key: 'taxable',
        label: 'Chargeable income',
        align: 'right'
      }, {
        key: 'paye',
        label: 'PAYE',
        align: 'right'
      }],
      rows: run.lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        tpin: l.tpin ?? '',
        gross: l.gross,
        taxable: l.taxable,
        paye: l.paye
      }))
    },
    napsa: {
      columns: [{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'member',
        label: 'NAPSA no.'
      }, {
        key: 'gross',
        label: 'Gross',
        align: 'right'
      }, {
        key: 'employee',
        label: 'Employee share',
        align: 'right'
      }, {
        key: 'employer',
        label: 'Employer share',
        align: 'right'
      }, {
        key: 'total',
        label: 'Total',
        align: 'right'
      }],
      rows: run.lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        member: l.napsaNumber ?? '',
        gross: l.gross,
        employee: l.napsaEmployee,
        employer: l.napsaEmployer,
        total: sumKwacha([l.napsaEmployee, l.napsaEmployer])
      }))
    },
    nhima: {
      columns: [{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'member',
        label: 'NHIMA no.'
      }, {
        key: 'basic',
        label: 'Basic',
        align: 'right'
      }, {
        key: 'employee',
        label: 'Employee share',
        align: 'right'
      }, {
        key: 'employer',
        label: 'Employer share',
        align: 'right'
      }, {
        key: 'total',
        label: 'Total',
        align: 'right'
      }],
      rows: run.lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        member: l.nhimaNumber ?? '',
        basic: l.basic,
        employee: l.nhimaEmployee,
        employer: l.nhimaEmployer,
        total: sumKwacha([l.nhimaEmployee, l.nhimaEmployer])
      }))
    },
    employer: {
      columns: [{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'napsa',
        label: 'NAPSA (employer)',
        align: 'right'
      }, {
        key: 'nhima',
        label: 'NHIMA (employer)',
        align: 'right'
      }, {
        key: 'sdl',
        label: 'SDL',
        align: 'right'
      }, {
        key: 'wc',
        label: 'Workers’ Comp',
        align: 'right'
      }, {
        key: 'total',
        label: 'Total employer',
        align: 'right'
      }],
      rows: run.lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        napsa: l.napsaEmployer,
        nhima: l.nhimaEmployer,
        sdl: l.sdl,
        wc: l.workersComp,
        total: sumKwacha([l.napsaEmployer, l.nhimaEmployer, l.sdl, l.workersComp])
      }))
    }
  };
  const {
    columns,
    rows
  } = config[kind];
  const totals: ExportRow = {
    number: 'TOTAL',
    name: `${rows.length} employees`
  };
  columns.filter(c => c.align === 'right').forEach(c => {
    totals[c.key] = sumKwacha(rows.map(r => Number(r[c.key]) || 0));
  });
  const title = `${kinds.find(k => k.id === kind)!.label} return — ${formatPeriod(run.period)}`;
  const file = `${kind.toUpperCase()}_${run.period}`;
  return <div className="space-y-5" data-id="element-1873" data-mp="2m31tain45egv">
      <div className="flex flex-wrap items-end gap-3" data-id="element-1874" data-mp="2m3bmef3gg1qs">
        <div data-id="element-1875" data-mp="2h2nvdtf87kwk">
          <label htmlFor="stat-run" className="block text-xs font-medium text-slate-500" data-id="element-1876" data-mp="2h1w3asku85b1">Payroll</label>
          <select id="stat-run" value={run.id} onChange={e => setRunId(e.target.value)} className="mt-1 rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-1877" data-mp="2c4m3ciztob8y">
            {finalised.map(r => <option key={r.id} value={r.id} data-id="element-1878" data-mp="34rgj52lp2mg3">{formatPeriod(r.period)} · {r.reference} ({r.status})</option>)}
          </select>
        </div>
        <p className="pb-2 text-xs text-slate-500" data-id="element-1879" data-mp="2zvi0zwqym0o4">Rules applied: {run.ruleSnapshot.label} (effective {run.ruleSnapshot.effectiveFrom})</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1880" data-mp="2r1wlgb6z0fdn">
        {summary.map(s => <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-1881" data-mp="3nk1848y9w7em">
            <p className="text-xs font-medium text-slate-500" data-id="element-1882" data-mp="3ir83dl2t0z2j">{s.label}</p>
            <p className="text-xl font-bold tabular-nums text-slate-900" data-id="element-1883" data-mp="1gu7e4aldlk9l">{formatKwacha(s.value)}</p>
            <p className="truncate text-xs text-slate-500" data-id="element-1884" data-mp="1lud7pwb7hgub">{s.note}</p>
          </div>)}
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1885" data-mp="175pi06o1k1ca">
        <header className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3" data-id="element-1886" data-mp="1c38vv5gtuc3p">
          <div role="tablist" aria-label="Contribution type" className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" data-id="element-1887" data-mp="y3zpexifawcx">
            {kinds.map(k => <button key={k.id} role="tab" type="button" aria-selected={kind === k.id} onClick={() => setKind(k.id)} className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${kind === k.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1888" data-mp="130zm4htdumrd">
                {k.label}
              </button>)}
          </div>
          <div className="ml-auto flex flex-wrap gap-2" data-id="element-1889" data-mp="2ninq41hnipv4">
            <Button size="sm" variant="outline" onClick={() => exportPdf(file, title, `${run.reference} · BMED Medical Centre`, columns, rows, totals)} data-id="element-1890" data-mp="bvasry9lhacx"><DownloadIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-1891" data-mp="6yc07k5kxbgu" /> PDF</Button>
            <Button size="sm" variant="outline" onClick={() => exportExcel(file, title, columns, [...rows, totals])} data-id="element-1892" data-mp="lmovsg0r8sat">Excel</Button>
            <Button size="sm" variant="outline" onClick={() => exportCsv(file, columns, [...rows, totals])} data-id="element-1893" data-mp="gsu5uuuk3h3a">CSV</Button>
          </div>
        </header>
        <div className="overflow-x-auto" data-id="element-1894" data-mp="qli29ohmjj1h">
          <table className="min-w-full text-sm" data-id="element-1895" data-mp="qkgrzm8fn0kt">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-1896" data-mp="14l182amyqiam">
              <tr data-id="element-1897" data-mp="zmg9efnd06oa">{columns.map(c => <th key={c.key} className={`whitespace-nowrap px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`} data-id="element-1898" data-mp="zly5qxydaq6c">{c.label}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100 tabular-nums" data-id="element-1899" data-mp="31db4f59qs1ty">
              {rows.map((row, i) => <tr key={i} data-id="element-1900" data-mp="2fkduyh3r9532" data-mpi={i}>{columns.map(c => <td key={c.key} className={`whitespace-nowrap px-4 py-2.5 ${c.align === 'right' ? 'text-right' : ''}`} data-id="element-1901" data-mp="2fj2q0dykprad">{typeof row[c.key] === 'number' ? formatKwacha(row[c.key] as number) : row[c.key] || '—'}</td>)}</tr>)}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold tabular-nums" data-id="element-1902" data-mp="2u91lua2noqmo">
              <tr data-id="element-1903" data-mp="1wl9mpak1ffcg">{columns.map(c => <td key={c.key} className={`whitespace-nowrap px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`} data-id="element-1904" data-mp="1wm1lsvcufv7h">{typeof totals[c.key] === 'number' ? formatKwacha(totals[c.key] as number) : totals[c.key] ?? ''}</td>)}</tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>;
}